import React, { useState } from "react";
import { Form, Flex, theme } from "antd";
import type { UploadFile } from "antd";
import { HeaderApp } from "../../shared";
import type { QuoteProduct, UploadQuoteData } from "../types";
import { URL_FILES } from "../../../constans/apis";
import openNotification from "../../../utils/openNotification";
import { useCreateQuote } from "../hooks/useCreateQuote";
import { useUploadFile } from "../../../lib/files/hooks/useUploadFile";
import { useNavigate } from "react-router-dom";
import { useFetchClients } from "../../clients/hooks/useFetchClients";
import { useFetchConcepts } from "../../concepts/hooks/useFetchConcepts";
import type { ProductData } from "../../products/types";
import type { ConceptData } from "../types";
import QuoteForm from "../components/QuoteForm";

const NewQuote: React.FC = () => {
  const [form] = Form.useForm();
  const { token } = theme.useToken();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<QuoteProduct[]>([]);
  const { mutate: createQuoteMutation, isPending: isCreating } = useCreateQuote();
  const { mutateAsync: uploadFileMutation, isPending: isUploading } = useUploadFile();
  const { data: concepts = [], isLoading: isLoadingConcepts } = useFetchConcepts();
  const { data: clients = [], isLoading: isLoadingClients } = useFetchClients();
  const navigate = useNavigate();

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (selectedProducts.length === 0) {
        openNotification("error", "Error", "Debes agregar al menos un producto");
        return;
      }

      const payload: UploadQuoteData = {
        ...values,
        // La descripción y el precio capturados en la línea viajan tal cual:
        // se guardan en la cotización, no en el catálogo de productos.
        products: selectedProducts.map((item) => ({
          ...item,
          product_description: item.product_description?.trim() || item.product_name,
          product_price_sale: Number(item.product_price_sale ?? item.product_price) || 0,
        })),
        date_quote: values.date_quote.format("YYYY-MM-DD"),
        date_expiration: values.date_expiration.format("YYYY-MM-DD"),
        total_quote: Number(values.total_quote),
        uuid_file_quote: "",
        concepts: values.id_concept ? [{ id_concept: values.id_concept }] : [],
      };

      if (fileList.length > 0) {
        const resFile = await uploadFileMutation({
          file: fileList[0] as unknown as File,
          endpoint: URL_FILES,
          // @ts-ignore
          token: TOKEN_FILES,
        });
        if (resFile.status !== 201 && resFile.status !== 200) {
          openNotification("error", "Error al subir adjunto", resFile.message);
          return;
        }
        payload.uuid_file_quote = resFile.data ? resFile.data.UUID_file : "";
      }

      createQuoteMutation(payload, {
        onSuccess: () => {
          form.resetFields();
          setFileList([]);
          navigate("/cotizaciones");
        },
        onError: (error) => {
          openNotification("error", "Error", error.message);
        },
      });
    } catch {
      // validateFields rejected — Ant Design shows inline errors
    }
  };

  const handleChangeProduct = (data: ProductData | undefined) => {
    if (!data) return;
    setSelectedProducts((prev) => [
      ...prev,
      {
        ...data,
        // id_quote lo asigna el backend al crear la cotización.
        id_quote: 0,
        product_amount: 1,
        product_description: data.product_description || data.product_name,
        product_price_sale: data.product_price_sale ?? data.product_price,
      },
    ]);
  };

  const handleChangeAmount = (id_product: number, amount: number) => {
    setSelectedProducts((prev) =>
      prev.map((item) =>
        item.id_product === id_product ? { ...item, product_amount: amount } : item
      )
    );
  };

  // Descripción y precio de venta se editan por línea y solo viven en la
  // cotización: el producto del catálogo queda intacto.
  const handleProductLineChange = (updated: QuoteProduct) => {
    setSelectedProducts((prev) =>
      prev.map((item) => (item.id_product === updated.id_product ? updated : item))
    );
  };

  const handleRemoveProduct = (id_product: number) => {
    setSelectedProducts((prev) => prev.filter((item) => item.id_product !== id_product));
  };

  return (
    <Flex vertical style={{ background: token.colorBgLayout }}>
      <HeaderApp sectionName="Crear Cotización" showBack />
      <Flex
        vertical
        gap={28}
        style={{ maxWidth: 640, width: "100%", margin: "0 auto", padding: "28px 24px 100px" }}
      >
        <QuoteForm
          form={form}
          mode="create"
          selectedProducts={selectedProducts}
          onProductChange={handleChangeProduct}
          onAmountChange={handleChangeAmount}
          onRemoveProduct={handleRemoveProduct}
          onProductLineChange={handleProductLineChange}
          fileList={fileList}
          onFileListChange={setFileList}
          onUuidFileReady={() => { }}
          clients={clients}
          concepts={concepts as ConceptData[]}
          isLoadingClients={isLoadingClients}
          isLoadingConcepts={isLoadingConcepts}
          isSubmitting={isCreating || isUploading}
          onSubmit={handleSubmit}
        />
      </Flex>
    </Flex>
  );
};

export default NewQuote;
